/**
 * Branded nominal types. These are compile-time only — at runtime they are plain
 * numbers/strings, so there is zero overhead, but the type system prevents mixing
 * an EntityId with a TileIndex, a PlayerId with a Tick, etc.
 */

declare const brand: unique symbol;

/** A nominal (branded) type. `Brand<number, 'EntityId'>` is a number you cannot
 * accidentally assign from another number brand. */
export type Brand<T, B extends string> = T & { readonly [brand]: B };

/** Handle to an entity in the ECS world. */
export type EntityId = Brand<number, 'EntityId'>;

/** Identifies a player/faction (0 = neutral/gaia). */
export type PlayerId = Brand<number, 'PlayerId'>;

/** A simulation tick (monotonic, 20 per simulated second). */
export type Tick = Brand<number, 'Tick'>;

/** Flat index into a row-major tile grid (`y * width + x`). */
export type TileIndex = Brand<number, 'TileIndex'>;

/** Seed value driving all deterministic randomness for a match. */
export type Seed = Brand<number, 'Seed'>;

export const asEntityId = (n: number): EntityId => n as EntityId;
export const asPlayerId = (n: number): PlayerId => n as PlayerId;
export const asTick = (n: number): Tick => n as Tick;
export const asTileIndex = (n: number): TileIndex => n as TileIndex;
export const asSeed = (n: number): Seed => (n >>> 0) as Seed;

/** The neutral / environment owner. */
export const GAIA: PlayerId = 0 as PlayerId;
